/**
 * System event channels (TRUS-684 phase 1, TRUS-685 phase 2).
 *
 * An Inbox channel is connected to system events (RCA jobs, pull requests,
 * deploys; and from phase 2 the org's signatures, tasks, workflows and member
 * tasks); a "WeaveIn" bot then posts each event into the channel as an
 * ordinary message whose `metadata.system_event` carries the card. Staff can
 * also follow events themselves ("Events I follow": bell and/or push).
 *
 * Typed client for system_events_contract.md (phase 1) and
 * system_events_contract_v2.md sections A and C (phase 2):
 *  - GET  /api/messaging/system-events/types
 *  - GET  /api/messaging/channels/{id}/event-subscription
 *  - PUT  /api/messaging/channels/{id}/event-subscription
 *  - POST /api/messaging/channels/{id}/event-subscription/test
 *  - GET  /api/notifications/event-subscriptions/me
 *  - PUT  /api/notifications/event-subscriptions/me
 *
 * Parsing is defensive (like services/callsApi.ts): anything malformed reads
 * as "nothing", never a crash. A server without TRUS-684 404s the types
 * endpoint; `listTypes()` then returns [] and the panel offers nothing to edit.
 */

import { apiClient } from './apiClient';

export const MESSAGING_BASE = '/api/messaging';

/**
 * The event families, in display order, with the types the UI knows how to
 * label. A type the server sends in a family we know is shown in that family
 * even when it is not listed here; anything else goes under "Other".
 */
export const SYSTEM_EVENT_GROUPS: ReadonlyArray<{ id: SystemEventFamily; types: readonly string[] }> = [
  {
    id: 'agent',
    types: ['rca.held_for_review', 'rca.failed', 'rca.executed', 'rca.creator_replied'],
  },
  { id: 'git', types: ['pr.ready_to_merge', 'pr.merged'] },
  { id: 'deploy', types: ['deploy.succeeded', 'deploy.failed', 'deploy.released'] },
  { id: 'signatures', types: ['signature.requested', 'signature.completed', 'signature.declined'] },
  { id: 'tasks', types: ['task.assigned', 'task.due_soon', 'task.overdue', 'task.completed'] },
  {
    id: 'workflows',
    types: ['workflow.step_assigned', 'workflow.step_overdue', 'workflow.completed', 'workflow.failed', 'workflow.notification'],
  },
  { id: 'member_tasks', types: ['member_task.assigned', 'member_task.overdue', 'member_task.completed'] },
  { id: 'automation', types: [] },
];

/**
 * UI family ids. Phase 1 named the first three agent / git / deploy; the
 * contract's `family` field says agents / code / deploys — `familyFromServer`
 * maps one onto the other.
 */
export type SystemEventFamily =
  | 'agent'
  | 'git'
  | 'deploy'
  | 'signatures'
  | 'tasks'
  | 'workflows'
  | 'member_tasks'
  | 'automation'
  | 'other';

/** Which filters exist (contract A: `filters`). */
export type SystemEventFilterKind = 'programs' | 'assignees' | 'repos' | 'services';
const FILTER_KINDS: readonly SystemEventFilterKind[] = ['programs', 'assignees', 'repos', 'services'];

/** Repo filter values (contract: filters.repos). */
export const SYSTEM_EVENT_REPOS = ['backend', 'frontend', 'mobile'] as const;
export type SystemEventRepo = (typeof SYSTEM_EVENT_REPOS)[number];

/** Delivery modes for a channel subscription (contract C). */
export type SystemEventDeliveryMode = 'immediate' | 'hourly_digest';

/** Where a personal subscription notifies (contract C). */
export type PersonalEventChannel = 'bell' | 'push';

export interface SystemEventType {
  type: string;
  label: string;
  description: string;
  /** 'platform' (WeaveIn operations) or 'org' (this organization's work). */
  scope: string;
  can_subscribe: boolean;
  /** Carries member-linked data: private channels only, minimal text. */
  phi: boolean;
  family: SystemEventFamily;
  /** The filters that apply to this type. */
  filters: SystemEventFilterKind[];
}

/** An assignee filter entry: a user id, or "me" (personal subscriptions only). */
export type AssigneeFilter = number | 'me';

export interface SystemEventFilters {
  repos: string[];
  services: string[];
  programs: number[];
  assignees: AssigneeFilter[];
}

export const EMPTY_FILTERS: SystemEventFilters = { repos: [], services: [], programs: [], assignees: [] };

export interface EventSubscription {
  event_types: string[];
  filters: SystemEventFilters;
  is_active: boolean;
  delivery: { mode: SystemEventDeliveryMode };
  updated_at: string | null;
  updated_by: { user_id: number | null; name: string } | null;
}

export interface EventSubscriptionInput {
  event_types: string[];
  filters: SystemEventFilters;
  is_active: boolean;
  delivery?: { mode: SystemEventDeliveryMode };
}

/** "Events I follow" (contract C, personal subscriptions). */
export interface PersonalEventSubscription {
  event_types: string[];
  filters: SystemEventFilters;
  channels: PersonalEventChannel[];
  is_active: boolean;
  updated_at: string | null;
}

export interface PersonalEventSubscriptionInput {
  event_types: string[];
  filters: SystemEventFilters;
  channels: PersonalEventChannel[];
  is_active: boolean;
}

/** `metadata.system_event` on a bot message. */
export interface SystemEventMeta {
  id: number | null;
  event_type: string;
  /** From the server's `family` when sent (TRUS-685), else the type prefix. */
  family?: SystemEventFamily;
  title: string;
  body: string | null;
  link: string | null;
  entity_type: string | null;
  entity_id: string | null;
}

/** An error from these endpoints, with the backend's machine-readable `code`. */
export class SystemEventsApiError extends Error {
  status: number;
  code: string | null;
  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = 'SystemEventsApiError';
    this.status = status;
    this.code = code;
  }
}

export function errorFrom(err: any): SystemEventsApiError {
  const status = Number(err?.response?.status) || 0;
  const data = err?.response?.data;
  const detail = data?.detail;
  const code =
    (typeof data?.code === 'string' && data.code) ||
    (typeof detail?.code === 'string' && detail.code) ||
    (typeof detail === 'string' && /^[a-z_]+$/.test(detail) ? detail : null) ||
    null;
  const message =
    typeof detail === 'string'
      ? detail
      : typeof detail?.message === 'string'
        ? detail.message
        : err?.message || 'Request failed';
  return new SystemEventsApiError(message, status, code);
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const v of value) {
    if (typeof v === 'string' && v.trim() && !out.includes(v.trim())) out.push(v.trim());
  }
  return out;
}

/** The family an event type belongs to (drives the card icon and grouping). */
export function familyOf(eventType: string | null | undefined): SystemEventFamily {
  const t = String(eventType || '');
  if (t.startsWith('rca.')) return 'agent';
  if (t.startsWith('pr.')) return 'git';
  if (t.startsWith('deploy.')) return 'deploy';
  if (t.startsWith('signature.')) return 'signatures';
  if (t.startsWith('task.')) return 'tasks';
  if (t.startsWith('workflow.')) return 'workflows';
  if (t.startsWith('member_task.')) return 'member_tasks';
  if (t.startsWith('automation.')) return 'automation';
  return 'other';
}

const SERVER_FAMILY: Record<string, SystemEventFamily> = {
  agents: 'agent',
  agent: 'agent',
  code: 'git',
  git: 'git',
  deploys: 'deploy',
  deploy: 'deploy',
  signatures: 'signatures',
  tasks: 'tasks',
  workflows: 'workflows',
  member_tasks: 'member_tasks',
  automation: 'automation',
};

/** The UI family for the contract's `family` value; falls back to the type prefix. */
export function familyFromServer(family: unknown, eventType: string): SystemEventFamily {
  const mapped = typeof family === 'string' ? SERVER_FAMILY[family] : undefined;
  return mapped || familyOf(eventType);
}

/** Filters a type takes when the server does not say (a phase-1 server). */
export function defaultFilterKinds(family: SystemEventFamily): SystemEventFilterKind[] {
  if (family === 'git') return ['repos'];
  if (family === 'deploy') return ['services'];
  if (family === 'agent' || family === 'other') return [];
  return ['programs', 'assignees'];
}

function filterKinds(value: unknown, family: SystemEventFamily): SystemEventFilterKind[] {
  if (!Array.isArray(value)) return defaultFilterKinds(family);
  return FILTER_KINDS.filter((k) => value.includes(k));
}

export function parseTypes(data: unknown): SystemEventType[] {
  const list = (data as any)?.types;
  if (!Array.isArray(list)) return [];
  const out: SystemEventType[] = [];
  for (const raw of list) {
    const type = textOrNull(raw?.type);
    if (!type || out.some((t) => t.type === type)) continue;
    const family = familyFromServer(raw?.family, type);
    out.push({
      type,
      label: textOrNull(raw?.label) || type,
      description: typeof raw?.description === 'string' ? raw.description : '',
      scope: textOrNull(raw?.scope) || 'platform',
      can_subscribe: raw?.can_subscribe === true,
      phi: raw?.phi === true,
      family,
      filters: filterKinds(raw?.filters, family),
    });
  }
  return out;
}

function idList(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const out: number[] = [];
  for (const v of value) {
    const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : NaN;
    if (Number.isInteger(n) && n > 0 && !out.includes(n)) out.push(n);
  }
  return out;
}

function assigneeList(value: unknown): AssigneeFilter[] {
  if (!Array.isArray(value)) return [];
  const out: AssigneeFilter[] = value.some((v) => v === 'me') ? ['me'] : [];
  for (const id of idList(value)) out.push(id);
  return out;
}

export function parseFilters(value: unknown): SystemEventFilters {
  const v = value && typeof value === 'object' ? (value as any) : {};
  return {
    repos: stringList(v.repos),
    services: stringList(v.services),
    programs: idList(v.programs),
    assignees: assigneeList(v.assignees),
  };
}

function deliveryOf(value: unknown): { mode: SystemEventDeliveryMode } {
  const mode = (value as any)?.mode;
  return { mode: mode === 'hourly_digest' ? 'hourly_digest' : 'immediate' };
}

/** The filters as the contract's PUT body takes them (empty lists = all). */
export function serializeFilters(filters: SystemEventFilters): Record<string, unknown> {
  return {
    repos: [...(filters.repos ?? [])],
    services: [...(filters.services ?? [])],
    programs: [...(filters.programs ?? [])],
    assignees: [...(filters.assignees ?? [])],
  };
}

const PERSONAL_CHANNELS: readonly PersonalEventChannel[] = ['bell', 'push'];

export function parsePersonalSubscriptions(data: unknown): PersonalEventSubscription | null {
  const list = (data as any)?.subscriptions;
  const s = Array.isArray(list) ? list.find((x) => x && typeof x === 'object') : null;
  if (!s) return null;
  const channels = Array.isArray(s.channels)
    ? PERSONAL_CHANNELS.filter((c) => s.channels.includes(c))
    : ['bell' as PersonalEventChannel];
  return {
    event_types: stringList(s.event_types),
    filters: parseFilters(s.filters),
    channels,
    is_active: s.is_active !== false,
    updated_at: textOrNull(s.updated_at),
  };
}

export function parseSubscription(data: unknown): EventSubscription | null {
  const s = (data as any)?.subscription;
  if (!s || typeof s !== 'object') return null;
  const eventTypes = stringList(s.event_types);
  if (eventTypes.length === 0) return null;
  const by = s.updated_by;
  const byId = Number(by?.user_id);
  return {
    event_types: eventTypes,
    filters: parseFilters(s.filters),
    is_active: s.is_active !== false,
    delivery: deliveryOf(s.delivery),
    updated_at: textOrNull(s.updated_at),
    updated_by:
      by && typeof by === 'object'
        ? { user_id: Number.isFinite(byId) && byId > 0 ? byId : null, name: textOrNull(by.name) || '' }
        : null,
  };
}

/** The system-event card metadata on a message, if any. */
export function systemEventMetaOf(metadata: Record<string, any> | null | undefined): SystemEventMeta | null {
  const e = metadata?.system_event;
  if (!e || typeof e !== 'object') return null;
  const title = textOrNull(e.title);
  const eventType = textOrNull(e.event_type);
  if (!title || !eventType) return null;
  const id = Number(e.id);
  return {
    id: Number.isFinite(id) && id > 0 ? id : null,
    event_type: eventType,
    family: familyFromServer(e.family, eventType),
    title,
    body: textOrNull(e.body),
    link: textOrNull(e.link),
    entity_type: textOrNull(e.entity_type),
    entity_id: e.entity_id == null ? null : String(e.entity_id),
  };
}

export interface SystemEventsApi {
  /** [] when the server has no TRUS-684 yet (404) or the call fails. */
  listTypes(): Promise<SystemEventType[]>;
  getSubscription(channelId: number): Promise<EventSubscription | null>;
  saveSubscription(channelId: number, input: EventSubscriptionInput): Promise<EventSubscription | null>;
  sendTest(channelId: number): Promise<{ message_id: number | null }>;
  /** The viewer's own "Events I follow" (null when they follow nothing yet). */
  getMySubscription(): Promise<PersonalEventSubscription | null>;
  saveMySubscription(input: PersonalEventSubscriptionInput): Promise<PersonalEventSubscription | null>;
}

export const NOTIFICATIONS_BASE = '/api/notifications';
const mySubscriptionPath = `${NOTIFICATIONS_BASE}/event-subscriptions/me`;

const subscriptionPath = (channelId: number) => `${MESSAGING_BASE}/channels/${channelId}/event-subscription`;

export const systemEventsApi: SystemEventsApi = {
  async listTypes() {
    try {
      const res = await apiClient.get(`${MESSAGING_BASE}/system-events/types`);
      return parseTypes(res.data);
    } catch {
      return [];
    }
  },
  async getSubscription(channelId) {
    try {
      const res = await apiClient.get(subscriptionPath(channelId));
      return parseSubscription(res.data);
    } catch (err) {
      throw errorFrom(err);
    }
  },
  async saveSubscription(channelId, input) {
    try {
      const body: Record<string, unknown> = {
        event_types: [...input.event_types],
        filters: serializeFilters(input.filters),
        is_active: input.is_active,
      };
      if (input.delivery) body.delivery = { mode: input.delivery.mode };
      const res = await apiClient.put(subscriptionPath(channelId), body);
      return parseSubscription(res.data);
    } catch (err) {
      throw errorFrom(err);
    }
  },
  async sendTest(channelId) {
    try {
      const res = await apiClient.post(`${subscriptionPath(channelId)}/test`, {});
      const id = Number(res.data?.message_id);
      return { message_id: Number.isFinite(id) && id > 0 ? id : null };
    } catch (err) {
      throw errorFrom(err);
    }
  },
  async getMySubscription() {
    try {
      const res = await apiClient.get(mySubscriptionPath);
      return parsePersonalSubscriptions(res.data);
    } catch (err) {
      throw errorFrom(err);
    }
  },
  async saveMySubscription(input) {
    try {
      const res = await apiClient.put(mySubscriptionPath, {
        event_types: [...input.event_types],
        filters: serializeFilters(input.filters),
        channels: PERSONAL_CHANNELS.filter((c) => input.channels.includes(c)),
        is_active: input.is_active,
      });
      return parsePersonalSubscriptions(res.data);
    } catch (err) {
      throw errorFrom(err);
    }
  },
};
